import { StatisticsCategoryCompoundData, StatisticsItemLayoutData, StatisticsItemLocData, TextureData } from "../../../types/types";
import { AppSettings } from "../../managers/AppSettings";
import { LoaderFileKeys } from "../../constant/AppConstants";
import { ScrollPaneTitledListContent } from "../dealcomplete/ScrollPaneTitledListContent";
import { StatisticsListItem } from "./StatisticsListItem";
import { AppObjectPool } from "../../pooling/AppObjectPool";
import { StatisticsScreen } from "../StatisticsScreen";
import { ImmediateStatsItemKeys, IntermediateStatsItemKeys, StatisticsDataManager } from "../../managers/StatisticsDataManager";
import { VariationId } from "../TableScreen";
import { LayoutDataManager } from "../../managers/LayoutDataManager";
import { AILevel } from "../../ai/TableAI";


export class StatisticsCategoryContent
    extends ScrollPaneTitledListContent
{
    protected categoryData: StatisticsCategoryCompoundData;

    private itemBgTextureData: TextureData;
    private itemLabelTextStyle: Phaser.Types.GameObjects.Text.TextStyle;

    private itemPoolGroupKey: string = "StatisticsListItem";
    // live data retrieval is a bit complex and requires difficultyIndex and variationId
    private difficultyId: AILevel;
    private variationId: VariationId;
    private delimiterLineAlpha: number;


    public override Init(categoryData?: StatisticsCategoryCompoundData, difficultyId?: AILevel): void
    {
        // > items
        super.Init();

        this.delimiterLineAlpha = LayoutDataManager.GetSharedData().general.screen.item.delimiterLine.alpha;

        this.categoryData = categoryData;
        this.difficultyId = difficultyId;
        this.nodeTheme = categoryData.theme; // for base icon
        // this.name = "StatisticsCategoryContent";

        this.itemBgTextureData = {
            texture: LoaderFileKeys.ui,
            frame: "rect-2x2-transparent.png"
        };
        this.itemLabelTextStyle = {
            color: "#fff",
            fontSize: "40px",
            align: "left",
            fontFamily: AppSettings.fontFamilies.MontserratBold,
            metrics: AppSettings.metrics.montserratBold40,
        };
    }

    /**
     * Create list items from data set via @see Init
     */
    public Build(): void
    {
        // > build title
        super.Build();
        this.add(this.nsContentBg);

        // 
        this.categoryData.layout.items.forEach((itemLayoutData: StatisticsItemLayoutData, i: number)=>{
            const statisticsItem: StatisticsListItem = this.GetFromPoolOrNew(this.itemPoolGroupKey); // + Init + Build
            this.items.push(statisticsItem as any);
            this.add(statisticsItem);
        });

        this.UpdateFromLocalizationChange();
    }
    
    private GetFromPoolOrNew(groupKey: string): StatisticsListItem
    {
        let item: StatisticsListItem = AppObjectPool.PickObject(groupKey);
        if (item == null) {
            // const itemId: string = this.categoryData.layout.items[i].id;
            item = new StatisticsListItem(this.scene);
            item.Init(this.itemBgTextureData, this.itemLabelTextStyle);
            item.Build();
        }
        return item;
    }

    /**
     * Build   -> UpdateFromLocalizationChange
     * SetData -> UpdateFromLocalizationChange
     */
    public UpdateFromLocalizationChange(): void
    {
        // category title
        const sTitle: string = this.categoryData.loc.title;
        this.btnTitle.SetLabel(sTitle);

        // items
        // this.categoryData.loc.items.forEach((itemLocData: StatisticsItemLocData, i: number)=>{
        (this.items as unknown as StatisticsListItem[]).forEach((statisticsItem: StatisticsListItem, i: number)=>{
            const itemLayoutData: StatisticsItemLayoutData = this.categoryData.layout.items[i];
            // console.log(itemLayoutData.id);
            const itemLocData: StatisticsItemLocData = this.categoryData.loc.itemsById[itemLayoutData.id];
            const itemLabel: string = itemLocData.itemLabel;
            statisticsItem.SetData(
                itemLabel,
                this.GetImmediateStatisticsItemValueByKey(itemLayoutData.id),
                this.items.length != i+1
            );
        });
    }
    
    public SetData(
        categoryData: StatisticsCategoryCompoundData
    ): void
    {
        this.categoryData = categoryData;
        this.nodeTheme = categoryData.theme;

        while(this.items.length) {
            const removedItem: StatisticsListItem = this.items.pop() as unknown as StatisticsListItem;
            AppObjectPool.DropObject(removedItem, this.itemPoolGroupKey);
        }

        this.categoryData.layout.items.forEach((itemLayoutData: StatisticsItemLayoutData, i: number)=>{
            const statisticsItem: StatisticsListItem = this.GetFromPoolOrNew(this.itemPoolGroupKey); // + Init + Build
            this.items.push(statisticsItem as any);
            this.add(statisticsItem);
        });
        
        this.sendToBack(this.nsContentBg);
        
        this.UpdateFromLocalizationChange();
    }

    /**
     * 
     * NOTE: you can further prettify percentages 
     *       (eg display integers without floating point)
     *       bu using Number.isInteger(value) ? .. : ..;
     * 
     * Reference: https://docs.google.com/document/d/1Fi5-2u0Y_zeLLl6xB0y_WdyWp4SFpN32tRkHjvobopE/edit#
     * 
     * @param id Unique item's data id. Based on this, we know how to parse (add prefix etc) the numerical data into string data to be displayed by a textfield;
     *      @see belote_layout.json/statistics 
     *      @see coinche_layout.json/statistics
     *      @see contree_layout.json/statistics
     * @param value 
     */
    private GetImmediateStatisticsItemValueByKey(immediateItemKey:string): string
    {
        let immediateResult: string;
        let n1: number;
        let n2: number;
        let n3: number;
        switch(immediateItemKey) {
            case ImmediateStatsItemKeys.numGamesPlayed:
                n1 = StatisticsDataManager.GetIntermediateStatByKey(IntermediateStatsItemKeys.numGamesPlayed, this.categoryData.variationId, this.difficultyId);
                immediateResult = n1.toString();
            break;
            case ImmediateStatsItemKeys.numGamesWonUs:
                n1 = StatisticsDataManager.GetIntermediateStatByKey(IntermediateStatsItemKeys.numGamesWonUs, this.categoryData.variationId, this.difficultyId);
                immediateResult = n1.toString();
            break;
            case ImmediateStatsItemKeys.percGamesWonUs:
                n1 = StatisticsDataManager.GetIntermediateStatByKey(IntermediateStatsItemKeys.numGamesWonUs, this.categoryData.variationId, this.difficultyId);
                n2 = StatisticsDataManager.GetIntermediateStatByKey(IntermediateStatsItemKeys.numGamesPlayed, this.categoryData.variationId, this.difficultyId);
                // console.log(n1, n2, ((n1 / n2) * 100))
                immediateResult = n2 == 0 ? "0%" : ((n1 / n2) * 100).toFixed(1).toString() + "%";
            break;
            case ImmediateStatsItemKeys.numDealsPlayed:
                n1 = StatisticsDataManager.GetIntermediateStatByKey(IntermediateStatsItemKeys.numDealsPlayed, this.categoryData.variationId, this.difficultyId);
                immediateResult = n1.toString();
            break;
            case ImmediateStatsItemKeys.totalScoreUs:
                n1 = StatisticsDataManager.GetIntermediateStatByKey(IntermediateStatsItemKeys.totalScoreUs, this.categoryData.variationId, this.difficultyId);
                immediateResult = n1.toString();
            break;
            case ImmediateStatsItemKeys.totalScoreThem:
                n1 = StatisticsDataManager.GetIntermediateStatByKey(IntermediateStatsItemKeys.totalScoreThem, this.categoryData.variationId, this.difficultyId);
                immediateResult = n1.toString();
            break;
            case ImmediateStatsItemKeys.avgScorePerDealUs:
                n1 = StatisticsDataManager.GetIntermediateStatByKey(IntermediateStatsItemKeys.totalScoreUs, this.categoryData.variationId, this.difficultyId);
                n2 = StatisticsDataManager.GetIntermediateStatByKey(IntermediateStatsItemKeys.numDealsPlayed, this.categoryData.variationId, this.difficultyId);
                immediateResult = n2 == 0 ? "0" : (n1 / n2).toFixed(1).toString();
            break;
            case ImmediateStatsItemKeys.avgScorePerDealThem:
                n1 = StatisticsDataManager.GetIntermediateStatByKey(IntermediateStatsItemKeys.totalScoreThem, this.categoryData.variationId, this.difficultyId);
                n2 = StatisticsDataManager.GetIntermediateStatByKey(IntermediateStatsItemKeys.numDealsPlayed, this.categoryData.variationId, this.difficultyId);
                immediateResult = n2 == 0 ? "0" : (n1 / n2).toFixed(1).toString();
            break;
            case ImmediateStatsItemKeys.avgScoreDifference:
                n1 = StatisticsDataManager.GetIntermediateStatByKey(IntermediateStatsItemKeys.totalScoreUs, this.categoryData.variationId, this.difficultyId);
                n2 = StatisticsDataManager.GetIntermediateStatByKey(IntermediateStatsItemKeys.totalScoreThem, this.categoryData.variationId, this.difficultyId);
                n3 = StatisticsDataManager.GetIntermediateStatByKey(IntermediateStatsItemKeys.numDealsPlayed, this.categoryData.variationId, this.difficultyId);
                immediateResult = n3 == 0 ? "0" : ((n1 - n2) / n3).toFixed(1).toString();
            break;
            case ImmediateStatsItemKeys.percAttackDealsUs:
                n1 = StatisticsDataManager.GetIntermediateStatByKey(IntermediateStatsItemKeys.numAttackDealsUs, this.categoryData.variationId, this.difficultyId);
                n2 = StatisticsDataManager.GetIntermediateStatByKey(IntermediateStatsItemKeys.numDealsPlayed, this.categoryData.variationId, this.difficultyId);
                immediateResult = n2 == 0 ? "0%" : ((n1 / n2) * 100).toFixed(1).toString() + "%";
            break;
            case ImmediateStatsItemKeys.percAttackDealsWonUs:
                n1 = StatisticsDataManager.GetIntermediateStatByKey(IntermediateStatsItemKeys.numAttackDealsWonUs, this.categoryData.variationId, this.difficultyId);
                n2 = StatisticsDataManager.GetIntermediateStatByKey(IntermediateStatsItemKeys.numAttackDealsUs, this.categoryData.variationId, this.difficultyId);
                immediateResult = n2 == 0 ? "0%" : ((n1 / n2) * 100).toFixed(1).toString() + "%";
            break;
            case ImmediateStatsItemKeys.percDefenseDealsUs:
                n1 = StatisticsDataManager.GetIntermediateStatByKey(IntermediateStatsItemKeys.numDefenseDealsUs, this.categoryData.variationId, this.difficultyId);
                n2 = StatisticsDataManager.GetIntermediateStatByKey(IntermediateStatsItemKeys.numDealsPlayed, this.categoryData.variationId, this.difficultyId);
                immediateResult = n2 == 0 ? "0%" : ((n1 / n2) * 100).toFixed(1).toString() + "%";
            break;
            case ImmediateStatsItemKeys.percDefenseDealsWonUs:
                n1 = StatisticsDataManager.GetIntermediateStatByKey(IntermediateStatsItemKeys.numDefenseDealsWonUs, this.categoryData.variationId, this.difficultyId);
                n2 = StatisticsDataManager.GetIntermediateStatByKey(IntermediateStatsItemKeys.numDefenseDealsUs, this.categoryData.variationId, this.difficultyId);
                immediateResult = n2 == 0 ? "0%" : ((n1 / n2) * 100).toFixed(1).toString() + "%";
            break;
            case ImmediateStatsItemKeys.percAttackDealsPlayer:
                n1 = StatisticsDataManager.GetIntermediateStatByKey(IntermediateStatsItemKeys.numAttackDealsPlayer, this.categoryData.variationId, this.difficultyId);
                n2 = StatisticsDataManager.GetIntermediateStatByKey(IntermediateStatsItemKeys.numDealsPlayed, this.categoryData.variationId, this.difficultyId);
                immediateResult = n2 == 0 ? "0%" : ((n1 / n2) * 100).toFixed(1).toString() + "%";
            break;
            case ImmediateStatsItemKeys.percAttackDealsWonPlayer:
                n1 = StatisticsDataManager.GetIntermediateStatByKey(IntermediateStatsItemKeys.numAttackDealsWonPlayer, this.categoryData.variationId, this.difficultyId);
                n2 = StatisticsDataManager.GetIntermediateStatByKey(IntermediateStatsItemKeys.numAttackDealsPlayer, this.categoryData.variationId, this.difficultyId);
                immediateResult = n2 == 0 ? "0%" : ((n1 / n2) * 100).toFixed(1).toString() + "%";
            break;
            case ImmediateStatsItemKeys.avgContractValueUs:
                n1 = StatisticsDataManager.GetIntermediateStatByKey(IntermediateStatsItemKeys.valueContractsUs, this.categoryData.variationId, this.difficultyId);
                n2 = StatisticsDataManager.GetIntermediateStatByKey(IntermediateStatsItemKeys.numAttackDealsUs, this.categoryData.variationId, this.difficultyId);
                immediateResult = n2 == 0 ? "0" : (n1 / n2).toFixed(1).toString();
            break;
            case ImmediateStatsItemKeys.numCapotsCalledUs:
                n1 = StatisticsDataManager.GetIntermediateStatByKey(IntermediateStatsItemKeys.numCapotsCalledUs, this.categoryData.variationId, this.difficultyId);
                // console.log("numCapotsCalledUs", VariationId[this.categoryData.variationId], DifficultyId[this.difficultyId], n1);
                immediateResult = n1.toString();
            break;
            case ImmediateStatsItemKeys.numCapotsWonUs:
                n1 = StatisticsDataManager.GetIntermediateStatByKey(IntermediateStatsItemKeys.numCapotsWonUs, this.categoryData.variationId, this.difficultyId);
                immediateResult = n1.toString();
            break;
            case ImmediateStatsItemKeys.numGeneralesCalledUs:
                n1 = StatisticsDataManager.GetIntermediateStatByKey(IntermediateStatsItemKeys.numGeneralesCalledUs, this.categoryData.variationId, this.difficultyId);
                immediateResult = n1.toString();
            break;
            case ImmediateStatsItemKeys.numGeneralesWonUs:
                n1 = StatisticsDataManager.GetIntermediateStatByKey(IntermediateStatsItemKeys.numGeneralesWonUs, this.categoryData.variationId, this.difficultyId);
                immediateResult = n1.toString();
            break;
            case ImmediateStatsItemKeys.numCoinchesCalledUs:
            case ImmediateStatsItemKeys.numDoublesCalledUs:
                n1 = StatisticsDataManager.GetIntermediateStatByKey(IntermediateStatsItemKeys.numContrasCalledUs, this.categoryData.variationId, this.difficultyId);
                immediateResult = n1.toString();
            break;
            case ImmediateStatsItemKeys.numCoinchesWonUs:
            case ImmediateStatsItemKeys.numDoublesWonUs:
                n1 = StatisticsDataManager.GetIntermediateStatByKey(IntermediateStatsItemKeys.numContrasWonUs, this.categoryData.variationId, this.difficultyId);
                immediateResult = n1.toString();
            break;
            case ImmediateStatsItemKeys.numSurCoinchesCalledUs:
            case ImmediateStatsItemKeys.numReDoublesCalledUs:
                n1 = StatisticsDataManager.GetIntermediateStatByKey(IntermediateStatsItemKeys.numReContrasCalledUs, this.categoryData.variationId, this.difficultyId);
                immediateResult = n1.toString();
            break;
            case ImmediateStatsItemKeys.numSurCoinchesWonUs:
            case ImmediateStatsItemKeys.numReDoublesWonUs:
                n1 = StatisticsDataManager.GetIntermediateStatByKey(IntermediateStatsItemKeys.numReContrasWonUs, this.categoryData.variationId, this.difficultyId);
                immediateResult = n1.toString();
            break;
            case ImmediateStatsItemKeys.numCoinchesCalledThem:
            case ImmediateStatsItemKeys.numDoublesCalledThem:
                n1 = StatisticsDataManager.GetIntermediateStatByKey(IntermediateStatsItemKeys.numContrasCalledThem, this.categoryData.variationId, this.difficultyId);
                immediateResult = n1.toString();
            break;
            case ImmediateStatsItemKeys.numCoinchesWonThem:
            case ImmediateStatsItemKeys.numDoublesWonThem:
                n1 = StatisticsDataManager.GetIntermediateStatByKey(IntermediateStatsItemKeys.numContrasWonThem, this.categoryData.variationId, this.difficultyId);
                immediateResult = n1.toString();
            break;
            
            default:
                console.log("Unknown item key:", immediateItemKey);
            return "";
        }

        return immediateResult;
    }

    public override SetWidth(width: number): void
    {
        super.SetWidth(width);
        
        this.RedrawBorder();
    }
}